import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  AlertTriangle, Car, ChevronLeft, Euro, Clock, CheckCircle,
  Loader2, Filter
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { formatCurrency, formatDate } from "../../lib/fleet-constants";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_OPTIONS = [
  { value: "all", label: "Tous statuts" },
  { value: "declared", label: "Déclaré" },
  { value: "in_progress", label: "En cours" },
  { value: "repair_scheduled", label: "Réparation planifiée" },
  { value: "repaired", label: "Réparé" },
  { value: "closed", label: "Clôturé" },
];

const STATUS_COLORS = {
  declared: "bg-blue-100 text-blue-800",
  in_progress: "bg-amber-100 text-amber-800",
  repair_scheduled: "bg-purple-100 text-purple-800",
  repaired: "bg-green-100 text-green-800",
  closed: "bg-slate-100 text-slate-800",
};

const CLAIM_TYPE_LABELS = {
  accident: "Accident",
  theft: "Vol",
  vandalism: "Vandalisme",
  glass_breakage: "Bris de glace",
  fire: "Incendie",
  natural_disaster: "Catastrophe naturelle",
  other: "Autre",
};

export default function ClaimsOverview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  
  const token = localStorage.getItem("token");

  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/fleet/claims?page=${page}&limit=50`;
      if (statusFilter !== "all") {
        url += `&status=${statusFilter}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaims(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error("Error fetching claims:", err);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [token, page, statusFilter]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const openCount = claims.filter(c => !["repaired", "closed"].includes(c.status)).length;
  const closedCount = claims.filter(c => ["repaired", "closed"].includes(c.status)).length;
  const totalCost = claims.reduce((sum, c) => sum + (c.repair_cost || c.deductible || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/fleet/dashboard")}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="w-10 h-10 rounded-lg bg-red-600 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Sinistres</h1>
              <p className="text-sm text-slate-500">{total} sinistre(s) • Vue transverse</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total sinistres</p>
                  <p className="text-2xl font-bold text-slate-900">{total}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card className={openCount > 0 ? "border-amber-200 bg-amber-50" : ""}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">En cours</p>
                  <p className="text-2xl font-bold text-amber-600">{openCount}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Résolus</p>
                  <p className="text-2xl font-bold text-green-600">{closedCount}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Coût total</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(totalCost)}</p>
                </div>
                <Euro className="w-8 h-8 text-red-300" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-500">Filtres:</span>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Véhicule</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Responsabilité</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Coût</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {claims.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Aucun sinistre trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  claims.map((claim) => (
                    <TableRow 
                      key={claim.id} 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/fleet/vehicles/${claim.vehicle_id}`)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Car className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="font-medium">{claim.vehicle_name}</p>
                            <p className="text-xs text-slate-500 font-mono">{claim.vehicle_registration}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(claim.claim_date)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {CLAIM_TYPE_LABELS[claim.claim_type] || claim.claim_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {claim.description}
                      </TableCell>
                      <TableCell>{claim.responsibility_label}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[claim.status] || "bg-slate-100"}>
                          {claim.status_label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {claim.repair_cost ? formatCurrency(claim.repair_cost) : 
                         claim.deductible ? formatCurrency(claim.deductible) : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
